<template>
  <div class="sizes-list">
    <div v-if="isMobile" class="sizes-list__title">
      {{ $t('editor.settings.size-title') }}
    </div>
    <v-spacer v-if="isMobile" />
    <v-select
      eager
      v-model="selectedSize"
      :items="sizes"
      hide-details
      class="select-size"
      :class="{ 'select-size--mobile': isMobile }"
      menu-icon="mdi-chevron-down"
      :menu-props="{ class: 'sizes-menu' }"
      @update:menu="selectFocusedHandler"
      ref="selectRef"
    >
      <template v-slot:prepend-item>
        <div
          class="item-sizes d-flex align-center"
          :class="{ 'item-sizes--active': selectedItemKey === -1 }"
          @click="selectSizeHandler({ width: customWidth || -1, height: customHeight || -1 }, -1)"
          @keyup="selectSizeHandler({ width: customWidth || -1, height: customHeight || -1 }, -1)"
        >
          <div class="d-flex align-center">
            <div class="item-sizes__icon">
              <inline-svg class="stroke" :src="require('@/assets/icons/sizes/custom.svg')" />
            </div>
            <div class="item-sizes__title">
              {{ $t('editor.settings.custom') }}
            </div>
          </div>
          <v-spacer />
          <div class="item-sizes__sizes d-flex align-center">
            <v-text-field v-model.number.lazy="customWidth" class="item-sizes__input" hide-details height="18" />
            <div class="item-sizes__divider">×</div>
            <v-text-field v-model.number.lazy="customHeight" class="item-sizes__input" hide-details />
          </div>
        </div>
      </template>
      <template v-slot:item="{ index, item, props }">
        <div
          :class="{ 'item-sizes--active': selectedItemKey === index }"
          class="item-sizes d-flex align-center"
          v-bind="props"
          @click="selectSizeHandler(item.value, index)"
          @keyup="selectSizeHandler(item.value, index)"
        >
          <div class="d-flex align-center">
            <div class="item-sizes__icon">
              <inline-svg
                v-if="item.value.width > item.value.height"
                class="fill"
                :src="require('@/assets/icons/sizes/horisontal.svg')"
              />
              <inline-svg
                v-else-if="item.value.width < item.value.height"
                class="fill"
                :src="require('@/assets/icons/sizes/vertical.svg')"
              />
              <inline-svg v-else class="fill" :src="require('@/assets/icons/sizes/square.svg')" />
            </div>
            <div class="item-sizes__title">
              {{ item.value.name }}
            </div>
          </div>
          <v-spacer />
          <div class="item-sizes__sizes d-flex align-center">
            {{ item.value.width }}
            <div class="item-sizes__divider">×</div>
            {{ item.value.height }}
          </div>
        </div>
      </template>
    </v-select>
    <ai-bottom-modal
      :show-modal="showSizesModal"
      :handle-toggle="toggleSizesModalHandler"
      :title="$t('editor.settings.size-title')"
      v-if="isMobile"
    >
      <div class="sizes-list__items">
        <ai-clear-button
          class="item-sizes d-flex align-center"
          :class="{ 'item-sizes--active': selectedItemKey === -1 }"
          @click="selectSizeHandler({ width: customWidth || 100, height: customHeight || 100 }, -1)"
        >
          <div class="d-flex align-center">
            <div class="item-sizes__icon">
              <inline-svg class="stroke" :src="require('@/assets/icons/sizes/custom.svg')" />
            </div>
            <div class="item-sizes__title">
              {{ $t('editor.settings.custom') }}
            </div>
          </div>
          <v-spacer />
          <div class="item-sizes__sizes d-flex align-center">
            <v-text-field
              @click.stop
              @blur="selectSizeHandler({ width: customWidth || 100, height: customHeight || 100 }, -1)"
              v-model.number.lazy="customWidth"
              class="item-sizes__input"
              hide-details
              height="18"
            />
            <div class="item-sizes__divider">×</div>
            <v-text-field
              @click.stop
              @blur="selectSizeHandler({ width: customWidth || 100, height: customHeight || 100 }, -1)"
              v-model.number.lazy="customHeight"
              class="item-sizes__input"
              hide-details
            />
          </div>
        </ai-clear-button>
        <div
          v-for="(item, index) in sizes"
          :key="index"
          :class="{ 'item-sizes--active': selectedItemKey === index }"
          class="item-sizes d-flex align-center"
          @click="selectSizeHandler(item, index)"
          @keyup="selectSizeHandler(item, index)"
        >
          <div class="d-flex align-center">
            <div class="item-sizes__icon">
              <inline-svg
                v-if="item.width > item.height"
                class="fill"
                :src="require('@/assets/icons/sizes/horisontal.svg')"
              />
              <inline-svg
                v-else-if="item.width < item.height"
                class="fill"
                :src="require('@/assets/icons/sizes/vertical.svg')"
              />
              <inline-svg v-else class="fill" :src="require('@/assets/icons/sizes/square.svg')" />
            </div>
            <div class="item-sizes__title">
              {{ item.name }}
            </div>
          </div>
          <v-spacer />
          <div class="item-sizes__sizes d-flex align-center">
            {{ item.width }}
            <div class="item-sizes__divider">×</div>
            {{ item.height }}
          </div>
        </div>
      </div>
    </ai-bottom-modal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, PropType } from 'vue';
import { storeToRefs } from 'pinia';

import { useCommonStore } from '@/store/common';
import { useEditorStore } from '@/store/editor';

import { ISize } from '@/types/ISize';
import { sizesEn } from '@/data/sizes/sizesEn';

import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiBottomModal from '@/components/UI/AiBottomModal/AiBottomModal.vue';

const sizes = ref<ISize[]>([]);

const props = defineProps({
  height: {
    type: Number as PropType<number>,
    required: true,
  },
  width: {
    type: Number as PropType<number>,
    required: true,
  },
});

const emits = defineEmits(['update:width', 'update:height']);

const { isMobile } = storeToRefs(useCommonStore());
const { generateImageData } = storeToRefs(useEditorStore());

const selectedSize = ref<string>('');
const selectedItemKey = ref<number>(-2);
const customWidth = ref<number | null>(null);
const customHeight = ref<number | null>(null);
const showSizesModal = ref<boolean>(false);
const selectRef = ref<any | null>(null);

const selectSizeHandler = (item: ISize, key: number): void => {
  if ((item.width > 0 && item.height > 0) || (item.width === 0 && item.height === 0)) {
    selectedSize.value = `${item.width} × ${item.height}`;
    selectedItemKey.value = key;
    emits('update:width', item.width);
    emits('update:height', item.height);
    showSizesModal.value = false;
  }
};

const toggleSizesModalHandler = (): void => {
  showSizesModal.value = !showSizesModal.value;
};

const sizesWatcher = (): void => {
  selectedSize.value = `${props.width} × ${props.height}`;
  const elemIndex = sizes.value.findIndex((size: ISize) => size.width === props.width && size.height === props.height);
  if (elemIndex !== -1) {
    selectSizeHandler(sizes.value[elemIndex], elemIndex);
    customWidth.value = null;
    customHeight.value = null;
  } else {
    selectSizeHandler({ width: props.width, height: props.height }, -1);
    customWidth.value = props.width;
    customHeight.value = props.height;
  }
};

const selectFocusedHandler = (): void => {
  if (isMobile.value) {
    showSizesModal.value = true;
    selectRef.value.menu = false;
  }
};

onMounted(() => {
  sizes.value = sizesEn;
  const { canvas } = generateImageData.value;
  const sizeIndex = sizes.value.findIndex(
    (size: ISize) => size.width === canvas.width && size.height === canvas.height,
  );
  if (sizeIndex !== -1) {
    selectSizeHandler(sizes.value[sizeIndex], sizeIndex);
    customWidth.value = null;
    customHeight.value = null;
  } else {
    selectSizeHandler(sizes.value[0], 0);
  }
});

watch(
  () => props.height,
  () => {
    sizesWatcher();
  },
);
watch(
  () => props.width,
  () => {
    sizesWatcher();
  },
);
</script>

<style scoped lang="scss" src="./SizesList.scss" />
